import { apiClient } from './api';
import { BaseService } from './BaseService';

export interface SalesReturnItem {
  variant_id: string;
  quantity: number;
  unit_price: number;
  description?: string;
  hsn_code?: string;
}

export interface SalesReturnCreate {
  invoice_id: string;
  return_reason: string;
  return_description?: string;
  return_date?: string;
  items: SalesReturnItem[];
}

export interface SalesReturnUpdate {
  return_reason?: string;
  return_description?: string;
  return_date?: string;
}

export interface SalesReturnStatusUpdate {
  status: string;
  notes?: string;
}

export interface SalesReturn {
  id: string;
  return_number: string;
  invoice_id: string;
  customer_id: string;
  return_date: string;
  return_reason: string;
  return_description?: string;
  status: string;
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total_amount: number;
  refund_amount: number;
  credit_note_number?: string;
  processed_at?: string;
  refunded_at?: string;
  created_by_admin_id: string;
  updated_by_admin_id?: string;
  processed_by_admin_id?: string;
  created_at: string;
  updated_at?: string;
  items: SalesReturnItemResponse[];
}

export interface SalesReturnItemResponse {
  id: string;
  sales_return_id: string;
  variant_id: string;
  quantity: number;
  unit_price: number;
  description?: string;
  hsn_code?: string;
  discount_amount: number;
  taxable_amount: number;
  tax_rate: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  tax_amount: number;
  line_total: number;
  created_at: string;
  updated_at?: string;
}

export interface SalesReturnSearchFilters {
  customer_id?: string;
  status?: string[];
  return_reason?: string[];
  return_date_from?: string;
  return_date_to?: string;
  invoice_id?: string;
  invoice_number?: string;
  return_number?: string;
  search_term?: string;
  page?: number;
  per_page?: number;
}

export interface SalesReturnListResponse {
  items: SalesReturn[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface CreditNoteRequest {
  sales_return_id: string;
  credit_note_number?: string;
  notes?: string;
}

export interface CreditNoteResponse {
  credit_note_number: string;
  sales_return_id: string;
  amount: number;
  generated_at: string;
  notes?: string;
}

class SalesReturnService extends BaseService<SalesReturn, SalesReturnCreate, SalesReturnUpdate, SalesReturnSearchFilters> {
  protected baseUrl = '/admin/sales-returns';

  async createSalesReturn(data: SalesReturnCreate): Promise<SalesReturn> {
    return this.create(data);
  }

  async getSalesReturn(id: string): Promise<SalesReturn> {
    return this.getById(id);
  }

  async getSalesReturnByNumber(returnNumber: string): Promise<SalesReturn> {
    const response = await apiClient.get(`${this.baseUrl}/number/${returnNumber}`);
    return response.data;
  }

  async updateSalesReturn(id: string, data: SalesReturnUpdate): Promise<SalesReturn> {
    return this.update(id, data);
  }

  async deleteSalesReturn(id: string): Promise<void> {
    return this.delete(id);
  }

  async updateStatus(id: string, data: SalesReturnStatusUpdate): Promise<SalesReturn> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/status`, data);
    return response.data;
  }

  async processReturn(id: string): Promise<SalesReturn> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/process`);
    return response.data;
  }

  async generateCreditNote(returnId: string, request?: Partial<CreditNoteRequest>): Promise<CreditNoteResponse> {
    const response = await apiClient.post(`${this.baseUrl}/${returnId}/credit-note`, request || {});
    return response.data;
  }

  async getReturnableInvoices(search?: string): Promise<ReturnableInvoice[]> {
    const response = await apiClient.get(`${this.baseUrl}/returnable-invoices`, {
      params: search ? { search } : {}
    });
    return response.data;
  }

  async getReturnableInvoiceItems(invoiceId: string): Promise<ReturnableInvoiceItem[]> {
    const response = await apiClient.get(`${this.baseUrl}/returnable-items/${invoiceId}`);
    return response.data;
  }
}

export interface ReturnableInvoice {
  id: string;
  invoice_number: string;
  customer_name: string;
  invoice_date: string;
  total_amount: number;
}

export interface ReturnableInvoiceItem {
  id: string;
  variant_id: string;
  variant_name: string;
  sku: string;
  quantity: number;
  max_quantity: number;
  unit_price: number;
  line_total: number;
  tax_rate: number;
  hsn_code?: string;
}

export const salesReturnService = new SalesReturnService();
